import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BriefcaseIcon, CodeIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
const EASE = [0.16, 1, 0.3, 1] as const;
export function About() {
  const {
    t
  } = useLanguage();
  const SERVICES = [t.svcWebDev, t.svcEcommerce, t.svcAI, t.svcMonthly, t.svcDomain, t.svcEmail, t.svcPerformance, t.svcSEO];
  const EXPERIENCE = [{
    icon: BriefcaseIcon,
    role: 'Developer Team Lead',
    type: 'Current',
    desc: 'Directs web projects in a marketing agency, delivering custom solutions and full infrastructure management while mentoring design and junior teams.',
    current: true
  }, {
    icon: CodeIcon,
    role: 'Front End Developer',
    type: 'Past Experience',
    desc: 'Building performant, pixel-perfect interfaces across a range of client projects.',
    current: false
  }, {
    icon: RocketIcon,
    role: 'Founder — Personal Projects',
    type: 'Ongoing',
    desc: 'Conceiving, building, and growing products from scratch — including Crobot.ai.',
    current: false
  }];
  return <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 z-10 relative" data-id="element-16">
      <div className="max-w-[1400px] mx-auto" data-id="element-17">
        <div className="border-t border-border pt-16 md:pt-20" data-id="element-18">
          {/* Top grid: bio + services */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-20 md:mb-28" data-id="element-19">
            {/* Left: Text */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            ease: EASE
          }} data-id="element-20">
              <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-4 font-light" data-id="element-21">
                {t.aboutOverline}
              </p>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-light text-primary tracking-tight mb-6 leading-snug" data-id="element-22">
                {t.aboutTitle}
              </h2>
              <p className="text-secondary text-base leading-relaxed font-light mb-6" data-id="element-23">
                {t.aboutP1}
              </p>
              <p className="text-secondary text-base leading-relaxed font-light mb-6" data-id="element-24">
                {t.aboutP2}
              </p>
              <p className="text-secondary text-base leading-relaxed font-light" data-id="element-25">
                {t.aboutP3}
              </p>
            </motion.div>

            {/* Right: Services */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.15,
            ease: EASE
          }} className="lg:pt-12" data-id="element-26">
              <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-8 font-light" data-id="element-27">
                {t.servicesOverline}
              </p>
              <div className="space-y-0" data-id="element-28">
                {SERVICES.map((service, index) => <motion.div key={service} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.06,
                ease: EASE
              }} className="flex items-center justify-between py-4 border-b border-border" data-id="element-29">
                    <span className="text-primary text-base font-light tracking-wide" data-id="element-30">
                      {service}
                    </span>
                    <motion.span className="text-muted text-xs font-light" initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.5 + index * 0.06
                }} data-id="element-31">
                      0{index + 1}
                    </motion.span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>

          {/* Bottom: Education + Experience */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: EASE
        }} className="border-t border-border pt-10 md:pt-12 pb-0" data-id="element-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24" data-id="element-33">
              {/* Education */}
              <div data-id="element-34">
                <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-8 font-light" data-id="element-35">
                  Education
                </p>
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1,
                ease: EASE
              }} className="flex items-start gap-4 p-5 rounded-xl border border-border/60 bg-surface/20" data-id="element-36">
                  <div className="w-9 h-9 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5" data-id="element-37">
                    <GraduationCapIcon className="w-3.5 h-3.5 text-secondary/60" data-id="element-38" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-39">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-40">
                      <h3 className="text-primary font-display font-light text-sm leading-snug" data-id="element-41">
                        Rochester Institute of Technology
                      </h3>
                      <span className="text-[9px] text-secondary/30 tracking-[0.1em] uppercase font-light" data-id="element-42">
                        RIT · New York, USA
                      </span>
                    </div>
                    <p className="text-secondary/80 text-sm font-light leading-relaxed" data-id="element-43">
                      Bachelor's Degree
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Experience */}
              <div data-id="element-44">
                <p className="text-secondary text-xs tracking-[0.2em] uppercase mb-8 font-light" data-id="element-45">
                  Experience
                </p>
                <div className="space-y-4" data-id="element-46">
                  {EXPERIENCE.map((item, i) => <motion.div key={item.role} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.1 + i * 0.1,
                  ease: EASE
                }} className="flex items-start gap-4 p-5 rounded-xl border border-border/60 bg-surface/20" data-id="element-47">
                      <div className="w-9 h-9 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5" data-id="element-48">
                        <item.icon className="w-3.5 h-3.5 text-secondary/60" data-id="element-49" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-50">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-51">
                          <h3 className="text-primary font-display font-light text-sm leading-snug" data-id="element-52">
                            {item.role}
                          </h3>
                          {item.current ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-green-500/20 bg-green-500/5" data-id="element-53">
                              <span className="w-1 h-1 rounded-full bg-green-400 inline-block" data-id="element-54" />
                              <span className="text-[9px] text-green-400/80 tracking-[0.1em] uppercase font-light" data-id="element-55">
                                {item.type}
                              </span>
                            </span> : <span className="text-[9px] text-secondary/30 tracking-[0.1em] uppercase font-light" data-id="element-56">
                              {item.type}
                            </span>}
                        </div>
                        <p className="text-secondary/80 text-sm font-light leading-relaxed" data-id="element-57">
                          {item.desc}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}